// Numbers as a trader reads them: compact dollars, signed percentages, and tiny prices with their
// run of zeros written as a subscript ($0.0₅1234 is $0.000001234).

const SUBSCRIPT = "₀₁₂₃₄₅₆₇₈₉";
const sub = (n: number) => [...String(n)].map((d) => SUBSCRIPT[Number(d)]).join("");

export function price(n: number | null | undefined, unit = "$"): string {
  if (n == null || !Number.isFinite(n)) return "–";
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(n);
  if (a === 0) return `${unit}0`;
  if (a >= 1000) return `${sign}${unit}${a.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  if (a >= 1) return `${sign}${unit}${a.toFixed(a >= 100 ? 2 : 4)}`;
  if (a >= 0.0001) return `${sign}${unit}${a.toPrecision(4)}`;
  // The zeros between the point and the first digit, and four significant digits after them.
  let zeros = Math.floor(-Math.log10(a));
  let digits = Math.round(a * 10 ** (zeros + 4));
  if (digits >= 10000) [zeros, digits] = [zeros - 1, 1000];
  return `${sign}${unit}0.0${sub(zeros)}${String(digits).replace(/0+$/, "")}`;
}

export function compact(n: number | null | undefined, unit = ""): string {
  if (n == null || !Number.isFinite(n)) return "–";
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(n);
  for (const [v, s] of [[1e12, "T"], [1e9, "B"], [1e6, "M"], [1e3, "K"]] as const) {
    if (a >= v) return `${sign}${unit}${(a / v).toFixed(a / v >= 100 ? 0 : 2)}${s}`;
  }
  return `${sign}${unit}${a >= 100 || Number.isInteger(a) ? a.toFixed(0) : a.toFixed(2)}`;
}

export const usd = (n: number | null | undefined) => compact(n, "$");

export function pct(f: number | null | undefined): string {
  if (f == null || !Number.isFinite(f)) return "–";
  const v = f * 100;
  const a = Math.abs(v);
  const s = a >= 10_000 ? compact(a) : a >= 100 ? a.toFixed(0) : a.toFixed(a >= 10 ? 1 : 2);
  return `${v > 0 ? "+" : v < 0 ? "-" : ""}${s}%`;
}

/** A share (0.42) as a percentage (42%). */
export function share(f: number | null | undefined): string {
  if (f == null || !Number.isFinite(f)) return "–";
  const v = f * 100;
  return `${v >= 10 || v === 0 ? v.toFixed(0) : v >= 0.1 ? v.toFixed(1) : "<0.1"}%`;
}

export function age(ms: number | null | undefined, now = Date.now()): string {
  if (ms == null) return "–";
  const s = Math.max(0, Math.floor((now - ms) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 86400 * 60) return `${Math.floor(s / 86400)}d`;
  if (s < 86400 * 365) return `${Math.floor(s / (86400 * 30))}mo`;
  return `${Math.floor(s / (86400 * 365))}y`;
}

export const short = (a: string) => (a.length > 12 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a);

/** A decimal amount string from the API: compact, and below 1 to three significant digits (0.00412). */
export function amount(s: string | null | undefined): string {
  const n = s == null ? null : Number(s);
  if (n == null || !Number.isFinite(n) || n === 0 || Math.abs(n) >= 1) return compact(n);
  return Math.abs(n) < 0.0001 ? price(n, "") : String(Number(n.toPrecision(3)));
}
